/**
 * Sync between Mapping panel state (midiCCMappings / midiNoteMappings)
 * and performance.rig.signals.
 */

/** @param {number} ch0 MIDI channel 0–15 */
function ch0to1(ch0) {
  return ch0 + 1;
}

/**
 * Build rig.signals from CC/note mapping objects (StateManager shape).
 * Preserves non-MIDI rig entries from `baseSignals` (e.g. audio_*, engine).
 * @param {Record<string, { channel: number, value: number, name?: string }>} ccMap
 * @param {Record<string, { channel: number, value: number, name?: string }>} noteMap
 * @param {Record<string, import('./schema.js').RigSignalDef>} [baseSignals]
 */
export function mappingsToRigSignals(ccMap, noteMap, baseSignals = {}) {
  const signals = { ...baseSignals };

  for (const m of Object.values(ccMap || {})) {
    const name = m?.name != null && String(m.name).trim();
    if (!name) continue;
    signals[name] = {
      type: 'midi_cc',
      channel: ch0to1(m.channel),
      cc: m.value,
      release: 0,
    };
  }

  for (const m of Object.values(noteMap || {})) {
    const name = m?.name != null && String(m.name).trim();
    if (!name) continue;
    signals[name] = {
      type: 'midi_note',
      channel: ch0to1(m.channel),
      note: m.value,
      release: 220,
    };
  }

  return signals;
}

/**
 * Strip midi_cc / midi_note entries derived from mappings; keep audio_band, audio_rms, engine.
 */
export function baseRigSignalsFromRig(rig) {
  const signals = { ...(rig?.signals || {}) };
  for (const [key, def] of Object.entries(signals)) {
    if (def?.type === 'midi_cc' || def?.type === 'midi_note') delete signals[key];
  }
  return signals;
}

/**
 * @param {Record<string, import('./schema.js').RigSignalDef>} signals
 * @returns {{ midiCCMappings: Record<string, { channel: number, value: number, name: string }>, midiNoteMappings: Record<string, { channel: number, value: number, name: string }> }}
 */
export function rigSignalsToMappings(signals) {
  /** @type {Record<string, { channel: number, value: number, name: string }>} */
  const midiCCMappings = {};
  /** @type {Record<string, { channel: number, value: number, name: string }>} */
  const midiNoteMappings = {};
  let ccI = 0;
  let noteI = 0;

  for (const [name, def] of Object.entries(signals || {})) {
    if (!def || typeof def !== 'object') continue;
    if (def.type === 'midi_cc' && def.channel != null && def.cc != null) {
      const id = `rig_cc_${ccI++}`;
      midiCCMappings[id] = {
        channel: def.channel - 1,
        value: def.cc,
        name,
      };
    }
    if (def.type === 'midi_note' && def.channel != null && def.note != null) {
      const id = `rig_note_${noteI++}`;
      midiNoteMappings[id] = {
        channel: def.channel - 1,
        value: def.note,
        name,
      };
    }
  }

  return { midiCCMappings, midiNoteMappings };
}
